import { useEffect, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { speak, stopNarration } from '../audio/narrator';
import { darkFirst } from '../live/learn';
import { TourStop, buildTour } from '../live/tour';
import { loadJSON, panel, place, saveJSON, selection, viewMode } from '../state';
import { Icon } from './icons';

export function TourPlayer() {
  const s = services.value!;
  const [stops, setStops] = useState<TourStop[] | null>(null);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(() => !!loadJSON<{ v?: boolean }>('tourMuted', {}).v);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build the tour; in daylight, jump to this evening first.
  useEffect(() => {
    let alive = true;
    (async () => {
      const jumped = !!(await darkFirst());
      const t = await buildTour(s, place.peek().name, jumped);
      if (alive) setStops(t);
    })();
    return () => {
      alive = false;
      stopNarration();
      if (timer.current) clearTimeout(timer.current);
      selection.value = null;
    };
  }, []);

  // Show each stop: fly there, highlight it and read it out.
  useEffect(() => {
    if (!stops) return;
    const stop = stops[i];
    if (timer.current) clearTimeout(timer.current);
    if (stop.enu && viewMode.peek() === 'manual') s.engine.flyTo(stop.enu, stop.fov, 1.6);
    selection.value = stop.sel ?? null;
    stopNarration();
    if (!playing) return;
    let cancelled = false;
    // On to the next stop only when this one was read to the end (not stopped, nor cut off by something else said).
    void speak(stop.clips, muted).then((finished) => {
      if (finished && !cancelled && i < stops.length - 1) timer.current = setTimeout(() => setI((k) => k + 1), 1100);
    });
    return () => {
      cancelled = true;
    };
  }, [stops, i, playing, muted]);

  const close = () => (panel.value = null);
  if (!stops) {
    return (
      <div class="tour">
        <div class="working"><div class="spinner" /><p>Preparing tonight’s tour…</p></div>
      </div>
    );
  }
  const stop = stops[i];
  return (
    <div class="tour" role="dialog" aria-label="Tonight's tour">
      <div class="tour-top">
        <span class="tour-step tnum">{i + 1} / {stops.length}</span>
        <button class={`icon-btn ${muted ? '' : 'on'}`} aria-label={muted ? 'Turn narration on' : 'Turn narration off'} onClick={() => { setMuted(!muted); saveJSON('tourMuted', { v: !muted }); }}>
          <Icon name={muted ? 'mute' : 'speaker'} size={18} />
        </button>
        <button class="icon-btn" aria-label="End tour" onClick={close}><Icon name="close" size={18} /></button>
      </div>
      <h3>{stop.title}</h3>
      <p>{stop.text}</p>
      <div class="tour-controls">
        <button class="btn-ghost" disabled={i === 0} onClick={() => setI(i - 1)} aria-label="Previous"><Icon name="back" size={16} /></button>
        <button class="btn-ghost" onClick={() => setPlaying(!playing)} aria-label={playing ? 'Pause' : 'Play'}><Icon name={playing ? 'pause' : 'play'} size={16} /></button>
        {i < stops.length - 1 ? (
          <button class="btn small" onClick={() => setI(i + 1)}>Next <Icon name="forward" size={14} /></button>
        ) : (
          <button class="btn small" onClick={close}>Finish</button>
        )}
      </div>
      <div class="tour-dots">{stops.map((_, k) => <span key={k} class={k === i ? 'on' : ''} />)}</div>
    </div>
  );
}
